import React, { useEffect, useContext, useState, useRef } from 'react'
import lodash from 'lodash'
import URL from 'url-parse'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router' // eslint-disable-line import/no-extraneous-dependencies
import { withTheme } from '@emotion/react/macro'
import { Redirect } from 'react-router-dom'
import { graphql } from '@apollo/client/react/hoc'

import CartSummaryUI from './cart-summary.component'
import gqlGetStoreInfo from '../../graphql/getStoreInfo'
import gqlCreatePayment from '../../graphql/createPayment'
import { CartContext } from '../../context/cart-context'
import { trackImpression } from '../../utils/analytics'
import { log, handleSessionError } from '../../utils/error-tracking'
import trackCartEvent from '../../utils/track-cart-event'
import { Loader } from '../../components/loader'
import clientData from '../../utils/client-data'

/**
 * Strip payment metadata fields before sending to mutations
 * @param {Array} items - Cart items array
 * @returns {Array} Cart items without payment metadata
 */
function stripPaymentMetadata(items) {
  return items.map((item) => {
    // eslint-disable-next-line no-unused-vars
    const { ecToken: _ecToken, paymentId: _paymentId, timestamp: _timestamp, ...cartItemWithoutMetadata } = item
    return cartItemWithoutMetadata
  })
}

/**
 * Constructs Apple App Clip URL with EC token
 * @param {string} ecToken - The EC token from payment creation
 * @returns {string} The constructed App Clip URL
 */
function constructAppClipUrl(ecToken) {
  // Get environment-specific AppClip bundle ID from config
  const appClipBundleId =
    clientData.appClip?.bundleId || 'com.yourcompany.PPClient.nativexoClip'

  // Use URLSearchParams to prevent injection attacks (JSDOM-compatible)
  const appSwitchParams = new URLSearchParams()
  appSwitchParams.set('token', ecToken)
  appSwitchParams.set('source', 'appclip')
  const appSwitchUrl = `https://www.paypal.com/app-switch-checkout?${appSwitchParams.toString()}`

  const appClipParams = new URLSearchParams()
  appClipParams.set('p', appClipBundleId)
  appClipParams.set('token', ecToken)
  appClipParams.set('url', appSwitchUrl)

  return `https://appclip.apple.com/id?${appClipParams.toString()}`
}

const CartSummary = ({ history, storeInfo, createPayment }) => {
  const {
    cartItems,
    loading,
    removeCartItem,
    updateCartItem,
    ecToken,
    isFetchingToken,
    soldOutPromotionsBySku,
    resetCartItems,
    isEcPrecomputeEnabled,
  } = useContext(CartContext)

  // Ref for AppClip redirect link
  const appClipRedirectLinkRef = useRef(null)

  // Local state for old flow (control group)
  const [loadingPaymentAdvice, setLoadingPaymentAdvice] = useState(false)
  const [soldOutPromotionsBySkuOldFlow, setSoldOutPromotionsBySkuOldFlow] =
    useState([])

  useEffect(() => {
    window.scrollTo(0, 0)

    trackImpression(
      'main:consumer:gifts:cart:',
      'main:consumer:gifts:cart:summary:::',
    )
  }, [])

  // Page Visibility API: Sync cart state when user returns to this tab
  // This handles the case where checkout completes in another tab and clears cart_id from localStorage
  useEffect(() => {
    const handleVisibilityChange = () => {
      // Only check when tab becomes visible (user returns to this tab)
      if (document.visibilityState === 'visible') {
        const cartId = localStorage.getItem('cart_id')

        // If cart_id was cleared (checkout completed in another tab)
        // but we still have stale data in React state, clear it
        if (!cartId && cartItems.length > 0) {
          log({
            file: 'cart-summary',
            err: {
              stack:
                'Cart was cleared in another tab (checkout completed), syncing state to show empty cart',
            },
          })
          resetCartItems()
        }
      }
    }

    // Listen for tab visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange)

    // Cleanup listener on component unmount
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [cartItems, resetCartItems])

  const logCheckoutError = (errMessage) => {
    log({
      file: 'cart-summary',
      err: { stack: errMessage },
    })

    trackCartEvent('cancelCart', [], {})
  }

  // Helper for OLD flow (control): get checkout origin
  const getCheckoutOrigin = () => {
    const parts = window.location.hostname.split('.')

    // if it's localhost, we should use msmaster for checkout
    if (parts.includes('localhost')) {
      return 'https://www.msmaster.qa.paypal.com'
    }

    // if it is stage, use the same stage
    // if it is not stage, use the same origin as current site
    return window.location.origin
  }

  // Handle payment errors in OLD flow (control)
  const handlePaymentError = (err) => {
    logCheckoutError(
      'Exception in create payment promise, redirect to error page',
    )

    const errorState = {}

    errorState.txnLimitErrors = lodash.flattenDeep(
      err.graphQLErrors.map((_err) => {
        try {
          return JSON.parse(_err.message)
        } catch (parseError) {
          return []
        }
      }),
    )

    if (errorState.txnLimitErrors.length) {
      history.push('/txnerror', errorState)
      return
    }

    history.push('/error')
  }

  // eslint-disable-next-line consistent-return
  const handleBuyNow = async () => {
    trackCartEvent('preparepurchase', cartItems)
    // ELMO BRANCHING: New flow (experiment) vs Old flow (control)
    if (isEcPrecomputeEnabled) {
      // ===== NEW FLOW (EXPERIMENT): EC token already precomputed =====
      if (!ecToken) {
        logCheckoutError('No EC token available, cannot proceed with checkout')
        return
      }

      // Redirect to AppClip using anchor tag for cross-device compatibility
      const appClipUrl = constructAppClipUrl(ecToken)
      if (appClipRedirectLinkRef.current) {
        appClipRedirectLinkRef.current.href = appClipUrl
        appClipRedirectLinkRef.current.click()
      }
    } else {
      // ===== OLD FLOW (CONTROL): Fetch EC token on Buy Now click =====
      setLoadingPaymentAdvice(true)

      let cartInfo = {}
      // Strip payment metadata before sending to createPayment mutation
      cartInfo.cartItems = stripPaymentMetadata(cartItems)

      try {
        const { data: createPaymentData } = await createPayment({
          variables: {
            cartInfo,
            stctype: new URL(window.location, true).query.stctype || '',
            sessionData: {
              utm_source:
                (sessionStorage && sessionStorage.getItem('utm_source')) || '',
            },
          },
        })

        const promotionSoldOutErrors = lodash.get(
          createPaymentData,
          'createPayment.promotionSoldOutErrors',
        )

        if (!lodash.isEmpty(promotionSoldOutErrors)) {
          logCheckoutError(
            'Promotions are sold out in cart, cancelling checkout and displaying error on cart',
          )
          setLoadingPaymentAdvice(false)
          // Set sold-out SKUs in local state to display alerts (old flow)
          setSoldOutPromotionsBySkuOldFlow(
            promotionSoldOutErrors.map((promoErr) => promoErr.sku),
          )
          return
        }

        const ecTokenFromPayment = lodash.get(
          createPaymentData,
          'createPayment.ecToken',
        )

        if (lodash.isEmpty(ecTokenFromPayment)) {
          logCheckoutError(
            'Could not get ecToken, cancelling cart and redirecting to error page',
          )

          history.push('/error')
          return
        }

        // redirect to Express Checkout (OLD behavior)
        const origin = getCheckoutOrigin()

        window.location.replace(
          `${origin}/cgi-bin/webscr?cmd=_express-checkout&useraction=commit&token=${createPaymentData.createPayment.ecToken}`,
        )
        return
      } catch (err) {
        handlePaymentError(err)
      }
    }
  }

  const handleRemoveItem = async (itemToRemove) => {
    removeCartItem(itemToRemove)
  }

  const handleUpdateItem = async (itemToUpdate, updatedItem) => {
    updateCartItem(itemToUpdate, updatedItem)
  }

  const handleEmptyCartBtnClick = () => {
    history.push('/all')
  }

  if (loading || storeInfo.loading || loadingPaymentAdvice) {
    return <Loader py="10rem" />
  }

  if (storeInfo.error) {
    handleSessionError(storeInfo.error)
    return <Redirect to="/error" />
  }

  // ELMO: Button disabled logic depends on which flow user is in
  const isBuyNowDisabled = isEcPrecomputeEnabled
    ? !ecToken || isFetchingToken // NEW flow: disabled until EC token ready
    : false // OLD flow: never disabled (EC token fetched on click)

  // Combine sold-out promotions from both flows
  const allSoldOutPromotions = isEcPrecomputeEnabled
    ? soldOutPromotionsBySku // NEW flow: from cart-context
    : soldOutPromotionsBySkuOldFlow // OLD flow: from local state

  return (
    <>
      {/* Hidden anchor tag for AppClip redirect - cross-device compatibility */}
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
      <a
        ref={appClipRedirectLinkRef}
        href="#"
        style={{ display: 'none' }}
        target="_self"
        rel="noopener noreferrer"
      >
        Redirect Link
      </a>
      <CartSummaryUI
        storeInfo={storeInfo}
        cartItems={cartItems}
        soldOutPromotionsBySku={allSoldOutPromotions}
        handleBuyNow={handleBuyNow}
        handleRemoveItem={handleRemoveItem}
        handleUpdateItem={handleUpdateItem}
        handleEmptyCartBtnClick={handleEmptyCartBtnClick}
        isBuyNowDisabled={isBuyNowDisabled}
      />
    </>
  )
}

CartSummary.propTypes = {
  history: PropTypes.object.isRequired,
  storeInfo: PropTypes.shape({
    loading: PropTypes.bool,
    error: PropTypes.object,
    getStoreInfo: PropTypes.shape({
      store_id: PropTypes.string,
      terms_and_conditions: PropTypes.string,
      locale: PropTypes.string,
    }),
  }),
  createPayment: PropTypes.func.isRequired,
}

export { CartSummary }

export default lodash.flowRight(
  withTheme,
  graphql(gqlGetStoreInfo, { name: 'storeInfo' }),
  graphql(gqlCreatePayment, { name: 'createPayment' }),
)(withRouter(CartSummary))
